import { getItemWithExpiry } from '@wavvest/shared-utils';
import { store } from '@/store/index';
import {
  setIsLoading,
  updateProgressState,
  updateVisualization,
  setCurrentThreadId,
  setIsLegacySession,
} from '@/store/features/canvas/canvasSlice';
import { selectUser } from '@/store/features/userinfo/userinfoSlice';
import {
  InterruptResume,
  MessageRequestBody,
  ResumeRequest,
  VisualizationRequest,
  WebSocketListener,
  WebSocketResponse,
} from '@/common/components/wavvest-planning/types';
import { extractVisualizationHtmlFromUpdate } from '@/common/components/wavvest-planning/orchestrator-message-adapter';
import config from '@/services/config';
import { createThread } from '@/api/weave-x-agents';
import type { AgentId } from '@/api/weave-x-agents';

type WebSocketConnectionFailure = {
  phase: 'auth' | 'handshake';
  timeToFailureMs?: number;
  closeCode?: number;
  closeReason?: string;
};

export type ReportCompletion = {
  threadId: string | null;
};

export type ReportCompletionListener = (completion: ReportCompletion) => void;

type CreateSessionOptions = {
  agentId?: AgentId;
  clientId?: string;
  investorId?: string;
};

const getUserClientId = (user: unknown): string | undefined => {
  if (!user || typeof user !== 'object') {
    return undefined;
  }

  const { client_id, clientId } = user as { client_id?: unknown; clientId?: unknown };
  const rawClientId = typeof client_id === 'string' && client_id.trim() ? client_id : clientId;

  return typeof rawClientId === 'string' && rawClientId.trim() ? rawClientId.trim() : undefined;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function extractFinalReportHtml(report: unknown): string {
  if (!isRecord(report)) return '';
  if (typeof report.viz_code === 'string') return report.viz_code;
  return typeof report.html === 'string' ? report.html : '';
}

function extractReleasedReportHtmlFromUpdate(payload: unknown): string {
  if (!isRecord(payload)) return '';
  const container = isRecord(payload.data) ? payload.data : payload;
  const releaseGate = isRecord(container.release_gate) ? container.release_gate : null;
  if (!releaseGate) return '';

  if (Array.isArray(releaseGate.report_history)) {
    for (const entry of [...releaseGate.report_history].reverse()) {
      if (!isRecord(entry)) continue;
      const html = extractFinalReportHtml(entry.final_report);
      if (html) return html;
    }
  }

  return isRecord(releaseGate.release_decision)
    ? extractFinalReportHtml(releaseGate.final_report)
    : '';
}

function fingerprintReportHtml(html: string): string {
  let hash = 2166136261;
  for (let index = 0; index < html.length; index += 1) {
    hash ^= html.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return `${html.length}:${hash >>> 0}`;
}

function serializeInterruptResume(resume: InterruptResume): Record<string, unknown> {
  switch (resume.kind) {
    case 'missing-data':
      return {
        kind: resume.kind,
        choice: resume.choice,
        token: resume.token,
        ...(resume.fillText ? { fill_text: resume.fillText } : {}),
      };
    case 'source-conflict':
      return {
        kind: resume.kind,
        conflict_id: resume.conflictId ?? resume.interruptId,
        resolution_token: resume.token,
        token: resume.token,
        conflict_signature: resume.conflictSignature,
        choice_kind: resume.choiceKind,
        ...(resume.selectedClaimId ? { selected_claim_id: resume.selectedClaimId } : {}),
        ...(resume.otherValue ? { other_message: resume.otherValue } : {}),
      };
    case 'staged-files':
      return {
        kind: resume.kind,
        token: resume.token,
        include: resume.include,
      };
    default:
      return resume;
  }
}

export class WebSocketService {
  private socket: WebSocket | null = null;
  private connected = false;
  private connectionPromise: Promise<boolean> | null = null;
  private resolveConnection: ((connected: boolean) => void) | null = null;
  private listeners: WebSocketListener[] = [];
  private reportCompletionListeners = new Set<ReportCompletionListener>();
  private completedReportFingerprintByThread = new Map<string, string>();
  private readonly wsUrl: string;
  private accessToken: string | null = null;
  private sessionId: string | null = null;
  private lastConnectionFailure: WebSocketConnectionFailure | null = null;

  constructor(url: string) {
    this.wsUrl = url;
  }

  public getConnectionStatus(): boolean {
    return this.connected && this.socket?.readyState === WebSocket.OPEN;
  }

  public getLastConnectionFailure(): WebSocketConnectionFailure | null {
    return this.lastConnectionFailure;
  }

  public addListener(listener: WebSocketListener): void {
    this.listeners.push(listener);

    if (this.getConnectionStatus() && listener.onConnect) {
      listener.onConnect();
    }
  }

  public removeListener(listener: WebSocketListener): void {
    this.listeners = this.listeners.filter((l) => l !== listener);
  }

  public addReportCompletionListener(listener: ReportCompletionListener): void {
    this.reportCompletionListeners.add(listener);
  }

  public removeReportCompletionListener(listener: ReportCompletionListener): void {
    this.reportCompletionListeners.delete(listener);
  }

  public async connect(): Promise<boolean> {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.connected = true;
      return true;
    }

    if (this.connectionPromise) {
      return this.connectionPromise;
    }

    try {
      this.accessToken = getItemWithExpiry('access_token');
      if (!this.accessToken) {
        console.error('Access token is missing or expired');
        this.lastConnectionFailure = { phase: 'auth' };
        return false;
      }

      const startedAt = Date.now();
      let opened = false;
      const socket = new WebSocket(this.wsUrl, this.accessToken as string);
      this.socket = socket;
      this.connected = false;
      this.lastConnectionFailure = null;

      this.connectionPromise = new Promise<boolean>((resolve) => {
        let settled = false;
        const settle = (connected: boolean) => {
          if (settled) {
            return;
          }
          settled = true;
          if (this.socket === socket) {
            this.connectionPromise = null;
            this.resolveConnection = null;
          }
          resolve(connected);
        };

        this.resolveConnection = settle;

        socket.onopen = () => {
          opened = true;
          if (this.socket !== socket) {
            socket.close();
            settle(false);
            return;
          }
          this.handleOpen();
          settle(true);
        };
        socket.onmessage = (event) => {
          if (this.socket === socket) {
            this.handleMessage(event);
          }
        };
        socket.onerror = (event) => {
          if (this.socket !== socket) {
            settle(false);
            return;
          }
          if (!opened) {
            this.lastConnectionFailure = {
              phase: 'handshake',
              timeToFailureMs: Date.now() - startedAt,
            };
          }
          this.handleError(event);
          settle(false);
        };
        socket.onclose = (event) => {
          if (this.socket !== socket) {
            settle(false);
            return;
          }
          if (!opened) {
            this.lastConnectionFailure = {
              phase: 'handshake',
              timeToFailureMs: Date.now() - startedAt,
              closeCode: event.code,
              closeReason: event.reason,
            };
          }
          this.handleClose();
          settle(false);
        };
      });

      return this.connectionPromise;
    } catch (error) {
      console.error('Error connecting to WebSocket:', error);
      this.notifyError(new Event('error'));
      this.connectionPromise = null;
      this.resolveConnection = null;
      return false;
    }
  }

  public send(message: unknown): boolean {
    if (!this.socket || !this.getConnectionStatus()) {
      console.error('Cannot send message: WebSocket is not connected');
      return false;
    }

    try {
      this.socket.send(typeof message === 'string' ? message : JSON.stringify(message));
      return true;
    } catch (error) {
      console.error('Error sending message:', error);
      return false;
    }
  }

  public sendVisualizationRequest(wsMessageBody: MessageRequestBody): boolean {
    const sessionId = this.getSessionId();

    if (!sessionId) {
      console.error('SessionId not found, please go to canvas page to initialize a session.');
      return false;
    }

    const user = selectUser(store.getState());
    const userId = user.id;
    const clientId = wsMessageBody.clientId || getUserClientId(user);

    const request: VisualizationRequest = {
      prompt: wsMessageBody.message,
      sessionId,
      userId,
      files: wsMessageBody.files,
      additionalContext: wsMessageBody.additionalContext,
      webSearchEnabled: wsMessageBody.webSearchEnabled,
      creativeMode: wsMessageBody.creativeMode,
      agentId: wsMessageBody.agentId,
      checkpointId: wsMessageBody.checkpointId,
      checkpoint: wsMessageBody.checkpoint,
      clientId,
      guidanceId: wsMessageBody.guidanceId,
      investorId: wsMessageBody.investorId,
    };
    return this.send(request);
  }

  /**
   * Send a resume request to continue an interrupted graph (ENG-2713).
   *
   * Mirrors sendVisualizationRequest but for the HITL resume path: an empty
   * prompt plus a per-kind `resume` object (built by the interrupt card). A
   * missing-data fill may also carry uploaded file references at the request
   * top level. The backend counterpart is the LangGraph SDK
   * `command={ resume: ... }` with `input=None` and transient file context.
   *
   * Uses the currently-active session via getSessionId() (kept in lockstep with
   * Redux) — it never creates or rotates a session; resuming a different thread
   * than the one that paused would abandon the checkpoint.
   */
  public sendResumeRequest(
    resume: InterruptResume,
    checkpoint?: { [key: string]: unknown },
  ): boolean {
    const sessionId = this.getSessionId();

    if (!sessionId) {
      console.error('SessionId not found, cannot resume the interrupted run.');
      return false;
    }

    const request: ResumeRequest = {
      prompt: '',
      sessionId,
      resume: serializeInterruptResume(resume),
      ...(resume.kind === 'missing-data' && resume.files?.length ? { files: resume.files } : {}),
      ...(checkpoint ? { checkpoint } : {}),
    };
    return this.send(request);
  }

  public disconnect(): void {
    if (this.socket) {
      this.resolveConnection?.(false);
      this.connectionPromise = null;
      this.resolveConnection = null;

      this.socket.onopen = null;
      this.socket.onmessage = null;
      this.socket.onerror = null;
      this.socket.onclose = null;

      this.socket.close();
      this.resetSessionId();
      this.socket = null;
    }

    this.connected = false;
  }

  private handleOpen(): void {
    this.connected = true;

    this.listeners.forEach((listener) => {
      if (listener.onConnect) {
        listener.onConnect();
      }
    });
  }

  private handleMessage(event: MessageEvent): void {
    try {
      const message = JSON.parse(event.data) as WebSocketResponse;
      const releasedReportHtml =
        message.type === 'updates' ? extractReleasedReportHtmlFromUpdate(message.payload) : '';
      const visualizationHtml =
        message.type === 'updates'
          ? releasedReportHtml || extractVisualizationHtmlFromUpdate(message.payload)
          : message.type === 'tool_result' && typeof message.payload?.html === 'string'
            ? message.payload.html
            : '';

      if (this.listeners.length === 0) {
        // canvas is unmounted, directly update to the store
        if (visualizationHtml) {
          store.dispatch(
            updateVisualization({
              sourceCode: visualizationHtml,
            }),
          );
          store.dispatch(setIsLoading(false));
          store.dispatch(
            updateProgressState({
              percent: 0,
              status: 'normal',
            }),
          );
        }
      } else {
        this.listeners.forEach((listener) => {
          listener.onMessage(message);
        });
      }

      if (releasedReportHtml) {
        this.notifyReportCompletion(releasedReportHtml);
      }
    } catch (error) {
      console.error('Error parsing WebSocket message:', error);
    }
  }

  private notifyReportCompletion(releasedReportHtml: string): void {
    const threadId = this.getSessionId();
    const threadKey = threadId ?? '__unknown_thread__';
    const reportFingerprint = fingerprintReportHtml(releasedReportHtml);

    if (this.completedReportFingerprintByThread.get(threadKey) === reportFingerprint) {
      return;
    }

    this.completedReportFingerprintByThread.set(threadKey, reportFingerprint);
    if (this.completedReportFingerprintByThread.size > 20) {
      const oldestThreadKey = this.completedReportFingerprintByThread.keys().next().value as
        string | undefined;
      if (oldestThreadKey) {
        this.completedReportFingerprintByThread.delete(oldestThreadKey);
      }
    }

    this.reportCompletionListeners.forEach((listener) => {
      listener({ threadId });
    });
  }

  private handleError(event: Event): void {
    console.error('WebSocket error:', event);
    this.notifyError(event);
  }

  private handleClose(): void {
    this.connected = false;

    this.listeners.forEach((listener) => {
      if (listener.onDisconnect) {
        listener.onDisconnect();
      }
    });
  }

  private notifyError(event: Event): void {
    this.listeners.forEach((listener) => {
      if (listener.onError) {
        listener.onError(event);
      }
    });
  }

  /**
   * @param sessionId
   * @description
   * Replace the current sessionId with another sessionId
   * Use when recovering from a session history or assigning a new sessionId
   */
  public updateSessionId(sessionId: string): void {
    this.sessionId = sessionId;
    store.dispatch(setCurrentThreadId(sessionId));
  }

  public resetSessionId(): void {
    this.sessionId = null;
    store.dispatch(setCurrentThreadId(null));
    store.dispatch(setIsLegacySession(false));
  }

  public getSessionId(): string | null {
    const currentThreadId = store.getState().canvas.currentThreadId;

    // Keep the websocket service's session in lockstep with Redux so send/cancel
    // flows resolve the same active thread even after a canvas reset or restore.
    if (currentThreadId !== this.sessionId) {
      this.sessionId = currentThreadId;
    }

    return this.sessionId;
  }

  /**
   * @description
   * Create a new session and replace the current sessionId
   * Use when starting a new session
   */
  public async createNewSession(options: CreateSessionOptions = {}): Promise<void> {
    const user = selectUser(store.getState());
    const { clientId, ...threadOptions } = options;
    const thread = await createThread(clientId || getUserClientId(user), user?.id, threadOptions);
    const sessionId = thread.thread_id;
    this.updateSessionId(sessionId);
    store.dispatch(setIsLegacySession(false));
  }
}

const canvasWebSocket = new WebSocketService(`${config.weaveXWebSocketBaseURL}/canvas/visualize`);

export default canvasWebSocket;
